import PageContainer from '@/components/PageContainer.js';
import useFirequery from '@/hooks/useFirequery';

import { clinicCrud, patientCrud, userCrud } from '@/services/firebase/firebase';
import { EntityItem, EntityList } from '@/components/EntityManagement';
import { Clinic, ClinicAdmin, Doctor, User } from '@/types/database';

import { FC, useMemo, useState } from 'react';
import { useAuth } from '@/components/auth';
import SelectClinic from '@/components/Select';
import { deleteClinic } from '@/services/api';
import { QueryArgs } from '@/services/firebase/FireCRUD';

interface Props {
  entity: 'doctor' | 'clinic-admin' | 'patient' | 'clinic';
}

const ENTITY_QUERY_MAP = {
  'clinic-admin': {
    query: (user: ClinicAdmin, clinicId?: string) => {
      const wheres: QueryArgs = [['role', '==', 'clinic-admin']];

      if (user.role === 'clinic-admin') {
        wheres.push(['managedClinicId', '==', user.managedClinicId]);
      } else if (user?.role === 'admin' && clinicId) {
        wheres.push(['managedClinicId', '==', clinicId]);
      }
      return userCrud.query(...wheres);
    },
    del: (id: string) => userCrud.update(id, { status: 'inactive' }),
  },
  doctor: {
    query: (user: ClinicAdmin, clinicId?: string) => {
      const wheres: QueryArgs = [['role', '==', 'doctor']];

      if (user.role === 'clinic-admin') {
        wheres.push(['associatedClinicIds', 'array-contains', user.managedClinicId]);
      } else if (user?.role === 'admin' && clinicId) {
        wheres.push(['associatedClinicIds', 'array-contains', clinicId]);
      }
      return userCrud.query(...wheres);
    },
    del: (id: string) => userCrud.update(id, { status: 'inactive' }),
  },
  clinic: {
    query: (user?: ClinicAdmin | User) => user && clinicCrud.query(),
    del: (id: string) => deleteClinic(id),
  },
};

const ClinicAdminManagement: FC<Props> = ({ entity }) => {
  const { user } = useAuth();
  const [selectedClinic, setSelectedClinic] = useState<{ value: string } | null>(null);
  const entityProps = ENTITY_QUERY_MAP[entity];
  const query = entityProps?.query(user, selectedClinic?.value);

  const clinicId = selectedClinic?.value || user?.managedClinicId;

  const { data: clinics, loading: loadingClinics } = useFirequery<Clinic[]>(clinicCrud.query(), {
    disabled: !query,
  });

  const selectItems = clinics?.map((clinic) => ({ label: clinic.name, value: clinic.id }));

  const { data: users, loading: loadingEntities } = useFirequery<ClinicAdmin[]>(query, {
    disabled: !query,
  });

  const onAction = (item: { id: string }, action: string) => {
    switch (action) {
      case 'Delete':
        entityProps?.del(item.id);
        break;
      default:
        break;
    }
  };

  const entities = useMemo(() => {
    return (
      users?.map((user) => {
        const clinicId = user.role === 'clinic-admin' ? user.managedClinicId : user.associatedClinicIds?.[0];

        return {
          id: user.id,
          primary: user.name,
          secondary: user.email,
          to: `clinics/${clinicId}/${entity}s/${user.id}`,
          icon:
            user.role === 'clinic-admin'
              ? 'eos-icons:admin'
              : user.role === 'doctor'
              ? 'vaadin:doctor'
              : user.role === 'patient'
              ? 'fluent:patient-24-filled'
              : '',
        };
      }) || []
    );
  }, [users, entity]);

  const title = entity === 'clinic-admin' ? 'manageClinicAdminsTitle' : entity === 'doctor' ? 'manageDoctorsTitle' : '';
  const description =
    entity === 'clinic-admin' ? 'manageClinicAdminsDescription' : entity === 'doctor' ? 'manageDoctorsDescription' : '';
  return (
    <PageContainer goback title={title} description={description}>
      {!user?.managedClinicId && (
        <SelectClinic
          fullWidth
          icon="healthicons:ambulatory-clinic-outline"
          loading={loadingClinics}
          label="Select Clinic"
          items={selectItems}
          onChange={(e, clinic) => setSelectedClinic(clinic)}
          value={selectedClinic}
        />
      )}
      <EntityList
        actions={['Delete']}
        disableCreate={!clinicId}
        toNew={`clinics/${clinicId}/${entity}s/new`}
        onClick={onAction}
        sx={{ mt: 5 }}
        title="Available"
        item={EntityItem}
        items={entities}
        loading={loadingEntities}
      />
    </PageContainer>
  );
};

export default ClinicAdminManagement;
